import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ArrowRight, X } from 'lucide-react';

/**
 * First-time Welcome banner for brand-new organisations.
 *
 * Replaces the old "Please onboard your properties" warning / multi-step
 * onboarding wizard. Points new users to the UBM Onboarding Planner.
 * Dismissible (per-account, remembered in localStorage).
 */
export default function WelcomeBanner({ account }) {
  const navigate = useNavigate();
  const dismissKey = account?.id ? `welcome_banner_dismissed:${account.id}` : null;
  const [hidden, setHidden] = useState(() => {
    try { return dismissKey ? localStorage.getItem(dismissKey) === '1' : false; }
    catch { return false; }
  });

  if (hidden) return null;

  const dismiss = () => {
    try { if (dismissKey) localStorage.setItem(dismissKey, '1'); } catch { /* ignore */ }
    setHidden(true);
  };

  return (
    <div
      data-testid="welcome-banner"
      className="relative overflow-hidden rounded-xl border border-blue-200 bg-gradient-to-r from-blue-50 via-white to-emerald-50 p-6 shadow-sm"
    >
      <div className="flex items-start gap-4">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[hsl(221,100%,33%)] text-white">
          <Sparkles className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Welcome to BillPortal
          </h2>
          <p className="mt-1.5 max-w-3xl text-sm leading-relaxed text-slate-600">
            Set up your entire portfolio, connect bill collection and prepare your workflows for free.
            You have <span className="font-semibold text-slate-900">5 free bill credits</span> to test
            processing — when you&apos;re ready to go live, simply add credits or upgrade your plan.
          </p>
          <button
            type="button"
            onClick={() => navigate('/ubm/onboarding-planner')}
            data-testid="welcome-banner-open-planner"
            className="mt-4 inline-flex items-center gap-2 rounded-md bg-[hsl(221,100%,33%)] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[hsl(221,100%,27%)]"
          >
            Open Onboarding Planner <ArrowRight className="h-4 w-4" />
          </button>
        </div>
        <button
          type="button"
          onClick={dismiss}
          data-testid="welcome-banner-close"
          aria-label="Dismiss welcome banner"
          className="shrink-0 rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
